/**
 * ProfilePhotoInputWithRemote Component
 *
 * Componente esteso per cattura foto profilo cliente con supporto foto remota.
 * Simile a DigitalizzaBigliettoPage ma senza OCR (solo foto volto).
 *
 * Features:
 * - Selezione sorgente: Remota, Webcam, Upload da PC
 * - Per "remota": usa RemotePhotoRequest
 * - Per "webcam": usa ProfilePhotoInput con camera
 * - Per "upload": file input con preview
 * - Preview circolare con zoom
 *
 * Usage:
 * <ProfilePhotoInputWithRemote
 *   value={customer.foto_profilo}
 *   onChange={(url) => setCustomer({ ...customer, foto_profilo: url })}
 *   customerName="Mario Rossi"
 *   userId="user-123"
 *   userName="Operatore"
 * />
 */

import React, { useState, useRef, useCallback } from 'react';
import { Camera, Upload, Wifi, X, User, ArrowLeft, Loader2 } from 'lucide-react';
import { ProfilePhotoInput } from './ProfilePhotoInput';
import { RemotePhotoRequest, type RemotePhotoResult } from '../remote-photo';

type PhotoSource = 'remote' | 'webcam' | 'upload' | null;

export interface ProfilePhotoInputWithRemoteProps {
  value?: string | null;           // URL foto esistente (da DB)
  onChange: (photoUrl: string | null) => void;  // Callback quando foto cambia
  customerName?: string;            // Nome cliente per upload
  customerId?: string;              // ID cliente se esistente
  userId: string;                   // ID utente desktop (per remote request)
  userName: string;                 // Nome utente desktop
  label?: string;                   // Label personalizzata
  helperText?: string;              // Testo helper
  required?: boolean;               // Campo obbligatorio
}

export function ProfilePhotoInputWithRemote({
  value,
  onChange,
  customerName = 'Cliente',
  customerId,
  userId,
  userName,
  label = 'Foto Profilo Cliente',
  helperText = 'Scatta una foto del volto del cliente per facilitare il riconoscimento',
  required = false
}: ProfilePhotoInputWithRemoteProps) {
  const [selectedSource, setSelectedSource] = useState<PhotoSource>(null);
  const [isChangingPhoto, setIsChangingPhoto] = useState(false); // Per distinguere "cambio foto" da "preview"
  const [showPhotoZoom, setShowPhotoZoom] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Gestisce la selezione della sorgente foto
   */
  const handleSourceSelect = (source: PhotoSource) => {
    setUploadError(null);

    if (source === 'upload') {
      // Trigger file input
      fileInputRef.current?.click();
      return;
    }

    setSelectedSource(source);
  };

  /**
   * Gestisce foto ricevuta da operatore mobile remoto
   */
  const handleRemotePhotoReceived = async (photo: RemotePhotoResult) => {
    setIsUploading(true);
    setUploadError(null);

    try {
      // Upload della foto al server
      const formData = new FormData();
      formData.append('photo', photo.blob, 'profile-photo.jpg');
      formData.append('customerName', customerName);
      if (customerId) {
        formData.append('customerId', customerId);
      }

      const response = await fetch('/api/accettazione-mobile/upload-customer-photo', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Errore upload foto');
      }

      const result = await response.json();

      // Backend ritorna { success: true, photo: { url, path, size } }
      if (result.success && result.photo?.url) {
        onChange(result.photo.url);
        setSelectedSource(null);
        setIsChangingPhoto(false); // Reset dopo upload riuscito
      } else {
        throw new Error(result.error || result.message || 'Errore upload');
      }
    } catch (err: any) {
      console.error('[ProfilePhotoInputWithRemote] Upload error:', err);
      setUploadError(err.message || 'Errore durante il caricamento');
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Gestisce file selezionato da PC
   */
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Seleziona un file immagine valido');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // Upload della foto al server
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('customerName', customerName);
      if (customerId) {
        formData.append('customerId', customerId);
      }

      const response = await fetch('/api/accettazione-mobile/upload-customer-photo', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Errore upload foto');
      }

      const result = await response.json();

      // Backend ritorna { success: true, photo: { url, path, size } }
      if (result.success && result.photo?.url) {
        onChange(result.photo.url);
        setIsChangingPhoto(false); // Reset dopo upload riuscito
      } else {
        throw new Error(result.error || result.message || 'Errore upload');
      }
    } catch (err: any) {
      console.error('[ProfilePhotoInputWithRemote] File upload error:', err);
      setUploadError(err.message || 'Errore durante il caricamento');
    } finally {
      setIsUploading(false);
      // Reset input for next selection
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  /**
   * Handler per foto catturata da webcam (usa ProfilePhotoInput esistente)
   */
  const handleWebcamPhotoChange = useCallback((url: string | null) => {
    if (url) {
      onChange(url);
      setSelectedSource(null);
      setIsChangingPhoto(false); // Reset dopo upload riuscito
    }
  }, [onChange]);

  /**
   * Rimuove la foto corrente
   */
  const handleRemovePhoto = () => {
    onChange(null);
    setSelectedSource(null);
  };

  /**
   * Torna alla selezione sorgente
   */
  const handleBackToSourceSelection = () => {
    setSelectedSource(null);
    setUploadError(null);
    // Se c'era una foto esistente e l'utente annulla, torna alla preview
    if (value) {
      setIsChangingPhoto(false);
    }
  };

  // Foto esistente: mostra preview con opzione modifica (ma non se stiamo cambiando foto)
  if (value && !selectedSource && !isChangingPhoto) {
    return (
      <div className="space-y-3">
        {/* Label */}
        <label className="block text-sm font-medium text-gray-300">
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>

        {/* Preview foto esistente */}
        <div className="border-2 border-green-500/30 rounded-xl p-4 bg-slate-800/50">
          <div className="flex items-center gap-4">
            {/* Preview circolare */}
            <div className="relative flex-shrink-0">
              <img
                src={value}
                alt="Foto profilo"
                className="w-20 h-20 rounded-full object-cover border-4 border-green-500 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setShowPhotoZoom(true)}
                title="Clicca per ingrandire"
              />
            </div>

            {/* Info e stato */}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-green-400 font-medium">Foto caricata</p>
              <p className="text-xs text-gray-400">Clicca sulla foto per ingrandire</p>
            </div>

            {/* Azioni */}
            <div className="flex gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsChangingPhoto(true)}
                className="p-2 bg-blue-600/20 hover:bg-blue-600/30 rounded-lg transition-colors"
                title="Cambia foto"
              >
                <Camera className="w-5 h-5 text-blue-400" />
              </button>
              <button
                type="button"
                onClick={handleRemovePhoto}
                className="p-2 bg-red-600/20 hover:bg-red-600/30 rounded-lg transition-colors"
                title="Rimuovi foto"
              >
                <X className="w-5 h-5 text-red-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Photo Zoom Modal */}
        {showPhotoZoom && (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setShowPhotoZoom(false)}
          >
            <div className="relative max-w-2xl max-h-[80vh] w-full flex items-center justify-center">
              <button
                onClick={() => setShowPhotoZoom(false)}
                className="absolute top-4 right-4 bg-gray-800/80 hover:bg-gray-700 text-white p-3 rounded-full transition-colors z-10"
                aria-label="Chiudi"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={value}
                alt="Foto profilo cliente"
                className="max-w-full max-h-full object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>
    );
  }

  // View: Remote Photo Request
  if (selectedSource === 'remote') {
    return (
      <div className="space-y-3">
        {/* Label */}
        <label className="block text-sm font-medium text-gray-300">
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>

        <div className="bg-slate-800/50 rounded-xl p-4 border border-purple-500/30">
          {/* Back button */}
          <button
            type="button"
            onClick={handleBackToSourceSelection}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Torna alla selezione</span>
          </button>

          {/* Remote Photo Request */}
          <RemotePhotoRequest
            onPhotoReceived={handleRemotePhotoReceived}
            onCancel={handleBackToSourceSelection}
            entityType="profile-photo"
            entityId={customerId || 'new-customer'}
            title="Foto Profilo Cliente"
            description={`Scatta una foto del volto del cliente${customerName ? ` (${customerName})` : ''}`}
            userId={userId}
            userName={userName}
            showChatHint={false}
          />
        </div>
      </div>
    );
  }

  // View: Webcam capture (usa ProfilePhotoInput esistente)
  if (selectedSource === 'webcam') {
    return (
      <div className="space-y-3">
        {/* Label */}
        <label className="block text-sm font-medium text-gray-300">
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>

        <div className="bg-slate-800/50 rounded-xl p-4 border border-blue-500/30">
          {/* Back button */}
          <button
            type="button"
            onClick={handleBackToSourceSelection}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Torna alla selezione</span>
          </button>

          {/* ProfilePhotoInput per webcam */}
          <ProfilePhotoInput
            value={null}
            onChange={handleWebcamPhotoChange}
            customerName={customerName}
            customerId={customerId}
            autoUpload={true}
            label=""
            helperText=""
            facingMode="user"
            enableCameraSwitch={true}
          />
        </div>
      </div>
    );
  }

  // Default View: Source Selection
  return (
    <div className="space-y-3">
      {/* Label */}
      <label className="block text-sm font-medium text-gray-300">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>

      {/* Helper text */}
      {helperText && (
        <p className="text-xs text-gray-400">
          {helperText}
        </p>
      )}

      {/* Error message */}
      {uploadError && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
          <p className="text-sm text-red-300">{uploadError}</p>
        </div>
      )}

      {/* Loading overlay */}
      {isUploading && (
        <div className="border-2 border-indigo-500/30 rounded-xl p-4 bg-indigo-900/10">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-indigo-400 animate-spin flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-indigo-300 font-medium">Upload in corso...</p>
              <p className="text-xs text-gray-400">Attendere prego</p>
            </div>
          </div>
        </div>
      )}

      {/* Source Selection (quando nessuna foto) */}
      {!isUploading && (
        <div className="border-2 border-dashed border-gray-600 rounded-xl p-6">
          <div className="flex flex-col items-center gap-4">
            {/* Icon placeholder */}
            <div className="w-20 h-20 bg-gray-700/50 rounded-full flex items-center justify-center">
              <User className="w-10 h-10 text-gray-500" />
            </div>

            {/* 3 Options Grid */}
            <div className="grid grid-cols-3 gap-3 w-full">
              {/* Option 1: Remote Photo */}
              <button
                type="button"
                onClick={() => handleSourceSelect('remote')}
                className="bg-gradient-to-br from-purple-600 to-indigo-700 hover:from-purple-500 hover:to-indigo-600 rounded-lg p-3 text-white shadow-lg transform transition-all hover:scale-105"
              >
                <div className="flex flex-col items-center space-y-2">
                  <Wifi className="w-6 h-6" />
                  <span className="text-xs font-medium">Remota</span>
                </div>
              </button>

              {/* Option 2: PC Webcam */}
              <button
                type="button"
                onClick={() => handleSourceSelect('webcam')}
                className="bg-gradient-to-br from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 rounded-lg p-3 text-white shadow-lg transform transition-all hover:scale-105"
              >
                <div className="flex flex-col items-center space-y-2">
                  <Camera className="w-6 h-6" />
                  <span className="text-xs font-medium">Webcam</span>
                </div>
              </button>

              {/* Option 3: Upload from PC */}
              <button
                type="button"
                onClick={() => handleSourceSelect('upload')}
                className="bg-gradient-to-br from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 rounded-lg p-3 text-white shadow-lg transform transition-all hover:scale-105"
              >
                <div className="flex flex-col items-center space-y-2">
                  <Upload className="w-6 h-6" />
                  <span className="text-xs font-medium">Carica</span>
                </div>
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              {required ? 'Foto richiesta' : 'Opzionale ma consigliata'}
            </p>

            {/* Pulsante Annulla se stiamo cambiando una foto esistente */}
            {isChangingPhoto && value && (
              <button
                type="button"
                onClick={() => setIsChangingPhoto(false)}
                className="mt-4 w-full py-2 px-4 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Annulla - Mantieni foto esistente</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />
    </div>
  );
}
